import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import useAnalytics from "./hooks/useAnalytics";
// Import all sections for home page
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import StrongerTogetherSection from "./components/StrongerTogetherSection";
import HistoryTimelineSection from "./components/HistoryTimelineSection";
import MissionSection from "./components/MissionSection";
import AboutSection from "./components/AboutSection";
import StrategicVisionSection from "./components/StrategicVisionSection";
import CommunitySpotlightSection from "./components/CommunitySpotlightSection";
import OrganizationSection from "./components/OrganizationSection";
import QuoteSection from "./components/QuoteSection";
import MemorialSection from "./components/MemorialSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";

// Import program pages
import DCMenInECE from "./pages/DCMenInECE";
import ALAA from "./pages/ALAA";
import InfantToddlerInstitute from "./pages/InfantToddlerInstitute";
import ECEPublicGood from "./pages/ECEPublicGood";
import StrongerTogetherPage from "./pages/StrongerTogetherPage";
// Temporarily removed: import LegislativeAdvocacy from "./pages/LegislativeAdvocacy";
import PartnerPage from "./pages/PartnerPage";
import BoardOfDirectors from "./pages/BoardOfDirectors";
import CommunityPage from "./pages/CommunityPage";
import WorkPriorities from "./pages/WorkPriorities";
import ECEProfessionalDevelopment from "./pages/ECEProfessionalDevelopment";
import AdminPage from "./pages/AdminPage";
// Archived (Sept 2026): import HC4CC from "./pages/HC4CC";
// Archived (Sept 2026): import HC4CCInquiryPage from "./pages/HC4CCInquiryPage";
import FaithUCCMobileMarket from "./pages/FaithUCCMobileMarket";
import PayEquityProof from "./pages/PayEquityProof";
import StorybookSubscribe from "./pages/StorybookSubscribe";
import PetitionSign from "./pages/PetitionSign";
import ConveningRegister from "./pages/ConveningRegister";
import BoardInterestForm from "./pages/BoardInterestForm";
import IdealCandidate from "./pages/IdealCandidate";
import PartnerWithUs from "./pages/PartnerWithUs";
import AdminResetPassword from "./pages/AdminResetPassword";
import BudgetWinSignOn from "./pages/BudgetWinSignOn";
import CertificateSigningPage from "./pages/CertificateSigningPage";
import StrongBeginnings from "./pages/StrongBeginnings";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import BoardVisionGoals from "./pages/BoardVisionGoals";
import BoardConflictOfInterest from "./pages/BoardConflictOfInterest";
import NewsletterPage from "./pages/NewsletterPage";
import NewsletterArchive from "./pages/NewsletterArchive";
// Archived (Sept 2026): import PlayScienceInstitute from "./pages/PlayScienceInstitute";
// Archived (Sept 2026): import PlayScienceInstituteRegister from "./pages/PlayScienceInstituteRegister";

// Analytics Tracker Component
const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Header />
      <main id="main-content" tabIndex={-1}>
        <HeroSection />
        <AboutSection />
        <MissionSection />
        <HistoryTimelineSection />
        <StrongerTogetherSection />
        <StrategicVisionSection />
        <OrganizationSection />
        <QuoteSection />
        <MemorialSection />
        <CommunitySpotlightSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsTracker />
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/work-priorities" element={<WorkPriorities />} />
          <Route path="/professional-development" element={<ECEProfessionalDevelopment />} />
          <Route path="/programs/dc-men-in-ece" element={<DCMenInECE />} />
          <Route path="/programs/mselp" element={<ALAA />} />
          <Route path="/programs/infant-toddler-institute" element={<InfantToddlerInstitute />} />
          <Route path="/programs/ece-public-good" element={<ECEPublicGood />} />
          <Route path="/programs/stronger-together" element={<StrongerTogetherPage />} />
          <Route path="/strong-beginnings" element={<StrongBeginnings />} />
          <Route path="/stronger-together/video" element={<Navigate to="/strong-beginnings" replace />} />
          {/* Temporarily removed: <Route path="/programs/legislative-advocacy" element={<LegislativeAdvocacy />} /> */}
          <Route path="/partners/:partnerId" element={<PartnerPage />} />
          <Route path="/board-of-directors" element={<BoardOfDirectors />} />
          <Route path="/community" element={<CommunityPage />} />
          {/* Archived (Sept 2026): <Route path="/initiatives/hc4cc" element={<HC4CC />} /> */}
          {/* Archived (Sept 2026): <Route path="/initiatives/hc4cc/inquiry" element={<HC4CCInquiryPage />} /> */}
          <Route path="/initiatives/faith-ucc-mobile-market" element={<FaithUCCMobileMarket />} />
          <Route path="/pay-equity-proof" element={<PayEquityProof />} />
          <Route path="/storybook-subscribe" element={<StorybookSubscribe />} />
          <Route path="/petition" element={<PetitionSign />} />
          <Route path="/strongertogether" element={<ConveningRegister />} />
          <Route path="/board-interest" element={<BoardInterestForm />} />
          <Route path="/programs/mselp/ideal-candidate" element={<IdealCandidate />} />
          <Route path="/partner-with-us" element={<PartnerWithUs />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/sign-on/budget-win-thank-you" element={<BudgetWinSignOn />} />
          <Route path="/sign/:token" element={<CertificateSigningPage />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/board/vision-goals" element={<BoardVisionGoals />} />
          <Route path="/board/conflict-of-interest" element={<BoardConflictOfInterest />} />
          <Route path="/newsletter" element={<NewsletterArchive />} />
          <Route path="/newsletter/august-2026" element={<NewsletterPage />} />
          {/* Archived (Sept 2026): <Route path="/professional-development/play-science-institute" element={<PlayScienceInstitute />} /> */}
          {/* Archived (Sept 2026): <Route path="/professional-development/play-science-institute/register" element={<PlayScienceInstituteRegister />} /> */}
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
